import { Injectable } from '@angular/core'

/**
 * Cache for the Account List grid.
 *
 * The client list is very large (tens of thousands of rows) — far too big for
 * session/localStorage (~5 MB quota) — so it is stored via the Cache Storage
 * API (`window.caches`), whose quota is disk-based (hundreds of MB). That lets
 * the list survive in-app navigation AND a full reload (F5) / browser restart.
 *
 * An in-memory mirror sits in front so repeat navigation within a session is
 * synchronous and instant, without touching disk.
 *
 * The list holds client PII, so it is explicitly wiped:
 *  - on logout / forced logout (auth.service.ts), and
 *  - on the login screen's first load (defends against expired sessions that
 *    never ran a clean logout).
 *
 * Cache Storage requires a secure context (HTTPS or localhost). If it is not
 * available, we transparently fall back to the in-memory mirror only.
 */
@Injectable({ providedIn: 'root' })
export class AccountListCacheService {
  private static readonly CACHE_NAME = 'gd-account-list'
  private mem = new Map<string, any[]>()

  private get caches(): CacheStorage | null {
    return typeof window !== 'undefined' && 'caches' in window ? window.caches : null
  }

  private urlFor(key: string): string {
    return `/__cache__/account-list/${key}`
  }

  /** Instant in-memory read; null if not mirrored this session. */
  getSync(key: string): any[] | null {
    return this.mem.get(key) ?? null
  }

  /** Read from the in-memory mirror, falling back to Cache Storage on disk. */
  async get(key: string): Promise<any[] | null> {
    const inMem = this.mem.get(key)
    if (inMem) return inMem

    const store = this.caches
    if (!store) return null

    try {
      const cache = await store.open(AccountListCacheService.CACHE_NAME)
      const res = await cache.match(this.urlFor(key))
      if (!res) return null
      const data = (await res.json()) as any[]
      this.mem.set(key, data) // warm the mirror for next time
      return data
    } catch {
      return null
    }
  }

  /** Write to both the in-memory mirror and Cache Storage. */
  async set(key: string, data: any[]): Promise<void> {
    this.mem.set(key, data)

    const store = this.caches
    if (!store) return

    try {
      const cache = await store.open(AccountListCacheService.CACHE_NAME)
      const res = new Response(JSON.stringify(data), {
        headers: { 'content-type': 'application/json' },
      })
      await cache.put(this.urlFor(key), res)
    } catch {
      // Disk quota / write failure — the in-memory mirror still serves this
      // session; not fatal.
    }
  }

  /** Wipe everything: in-memory mirror and the on-disk cache. */
  async clear(): Promise<void> {
    this.mem.clear()

    const store = this.caches
    if (!store) return

    try {
      await store.delete(AccountListCacheService.CACHE_NAME)
    } catch {
      // Nothing to clear / unavailable.
    }
  }
}
